import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthContext } from "../contexts/auth";
import getServices from "../services/getServices";
import postService from "../services/postService";
import putService from "../services/putService";

const FORMULARIO_INICIAL = {
  name: "",
  username: "",
  email: "",
  is_active: true,
  postos: [],
  tipos_tarefa: [],
};

const mensagemErro = (data) => {
  if (!data) return "Não foi possível cadastrar o usuário cliente.";
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  return Object.values(data).flat()[0] || "Não foi possível cadastrar o usuário cliente.";
};

const useUsuarioClienteFormulario = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { signOut } = useContext(AuthContext);
  const [submit, setSubmit] = useState(false);
  const [formulario, setFormulario] = useState(FORMULARIO_INICIAL);
  const [postos, setPostos] = useState([]);
  const [tiposTarefa, setTiposTarefa] = useState([]);
  const [senhaProvisoria, setSenhaProvisoria] = useState("");

  const alterarCampo = (campo, valor) => {
    setFormulario((atual) => ({ ...atual, [campo]: valor }));
  };

  const carregarOpcoes = async () => {
    setSubmit(true);
    const requisicoes = [
      getServices("admin/postosponto/"),
      getServices("admin/templateformulario/"),
    ];
    if (id) requisicoes.push(getServices(`admin/usuarios-clientes/${id}/`));
    const [resultadoPostos, resultadoTipos, resultadoUsuario] = await Promise.all(requisicoes);

    if (resultadoPostos.data?.code === "token_not_valid" || resultadoTipos.data?.code === "token_not_valid") {
      signOut();
      setSubmit(false);
      return;
    }

    if (resultadoPostos.type === "success") {
      setPostos(resultadoPostos.data.map((posto) => ({
        id: posto.id,
        nome: `${posto.codigo ? `${posto.codigo} - ` : ""}${posto.nome}`,
      })));
    } else {
      toast.error(mensagemErro(resultadoPostos.data));
    }

    if (resultadoTipos.type === "success") {
      const registros = resultadoTipos.data.results || resultadoTipos.data;
      const tiposUnicos = new Map();
      registros.forEach((registro) => {
        if (registro.tipotarefa?.id && registro.tipotarefa.editavel === true) {
          tiposUnicos.set(registro.tipotarefa.id, {
            id: registro.tipotarefa.id,
            nome: registro.tipotarefa.nome,
          });
        }
      });
      setTiposTarefa(Array.from(tiposUnicos.values()));
    } else {
      toast.error(mensagemErro(resultadoTipos.data));
    }

    if (resultadoUsuario?.type === "success") {
      setFormulario({
        name: resultadoUsuario.data.name || "",
        username: resultadoUsuario.data.username || "",
        email: resultadoUsuario.data.email || "",
        is_active: resultadoUsuario.data.is_active,
        postos: resultadoUsuario.data.postos || [],
        tipos_tarefa: resultadoUsuario.data.tipos_tarefa || [],
      });
    } else if (resultadoUsuario) {
      toast.error(mensagemErro(resultadoUsuario.data));
      navigate("/gestor/usuarios-clientes");
    }
    setSubmit(false);
  };

  useEffect(() => {
    carregarOpcoes();
  }, []);

  const salvar = async () => {
    if (!formulario.name.trim() || !formulario.username.trim() || !formulario.email.trim()) {
      toast.error("Preencha nome, usuário e e-mail.");
      return;
    }
    if (/\s/.test(formulario.username)) {
      toast.error("O Usuário Login não pode conter espaços em branco.");
      return;
    }

    if (!formulario.postos.length || !formulario.tipos_tarefa.length) {
      toast.error("Selecione pelo menos um posto e um tipo de tarefa.");
      return;
    }

    setSubmit(true);
    const numerosAleatorios = new Uint32Array(1);
    window.crypto.getRandomValues(numerosAleatorios);
    const novaSenhaProvisoria = `@${String(numerosAleatorios[0] % 100000000).padStart(8, "0")}`;

    const dados = {
      name: formulario.name.trim(),
      username: formulario.username.trim(),
      email: formulario.email.trim(),
      is_active: formulario.is_active,
      postos: formulario.postos.map((posto) => posto.id),
      tipos_tarefa: formulario.tipos_tarefa.map((tipo) => tipo.id),
    };
    if (!id) dados.password = novaSenhaProvisoria;
    const resultado = id
      ? await putService(`admin/usuarios-clientes/${id}/`, dados)
      : await postService("admin/usuarios-clientes/", dados);

    if (resultado.type === "success") {
      if (id) {
        toast.success("Usuário cliente atualizado com sucesso.");
        navigate("/gestor/usuarios-clientes");
      } else {
        setSenhaProvisoria(novaSenhaProvisoria);
      }
    } else if (resultado.data?.code === "token_not_valid") {
      signOut();
    } else {
      toast.error(mensagemErro(resultado.data));
    }
    setSubmit(false);
  };

  return {
    submit,
    formulario,
    postos,
    tiposTarefa,
    senhaProvisoria,
    editando: Boolean(id),
    alterarCampo,
    salvar,
  };
};

export default useUsuarioClienteFormulario;
